<template>
  <div class="table-wrapper">
    <statement-filter v-if="getterPopupName === 'StatementFilter'"
              @updateFilters="updateFilters" :txnOptions="txnOptions" />

    <statement-export v-else-if="getterPopupName === 'StatementExport'"
              :exportOptions="paginate" :exportRecords="exportRecords" />

    <statement-details v-else-if="getterPopupName === 'StatementDetails'"
              :txnDetails="popupDetails"
              @refreshList="refreshDetails" />

    <statement-preferences v-else-if="getterPopupName === 'StatementPreferences'"
              :txnFields="txnFields" @refreshList="refreshColumns" />

    <div class="table-title">
      <div class="subject">
        <p>Account Statement</p>

        <statement-webhooks v-if="list.length && !searchTerm"
                  :paginate="paginate" @resendWebhooks="bulkResendWebhooks" />
      </div>

      <div class="filters">
        <div class="options">
          <button @click="openPopup('StatementExport')">
            Export
            <i class="fa fa-share" />
          </button>
          <button :class="{'filters-applied': filtersApplied}"
                  @click="openPopup('StatementFilter')"
                  class="dark-btn">
            Filter
            <i class="fa fa-filter" />
          </button>
          <button @click="openPopup('StatementPreferences')" class="preferences">
            <i class="fa fa-cog" />
          </button>
        </div>

        <statement-search placeholder="Search ID / Ref number / Bank ref number"
                  :triggerSearch="triggerSearch" :searchTerm="searchTerm"
                  @populateList="clearSearch" @setFunctions="setSearchFunctions" />
      </div>

      <div
        v-if="filtersApplied"
        class="show-applied-filters"
      >
        <div
          v-for="(filter, key, index) in appliedFiltersObj"
          :key="index"
          class="badge"
        >
          <h5>{{key}}</h5>
          <p>{{filter}}</p>
        </div>
      </div>
    </div>

    <div class="table-head" v-if="isDesktop()">
      <p v-for="(field, index) in selectedFields" :key="index"
          :class="field.css">
        {{field.text}}
      </p>
    </div>

    <div class="table-body" >
      <div class="table-row cursor" v-for="(txn, index) in list" :key="index"
          @click="showDetails(txn)">
        <p v-for="(field, index) in selectedFields" :key="index"
            :class="{[field.css]: true}">
          <b v-if="!isDesktop()">{{field.shortenText}}:</b>
          <span :class="{[txn[field.key]]: field.key === 'credit_debit'}">
            {{field.isAmount ? amountFormat(txn[field.key]) : txn[field.key]}}
          </span>
          <i v-if="field.key === 'status' && txn[field.key] === 'PENDING'" class="fa fa-sync-alt"
            @click="refreshDetails($event, txn)" />
        </p>
      </div>
    </div>

    <table-loader v-if="!paginate.data_added || !paginate.stop || paginate.privileges_changed" />

    <div class="table-empty" v-else-if="list.length === 0">
      <p v-if="searchTerm">Unable to find transactions with <span class="highlight">{{searchTerm}}</span>.<br>Please search with the complete ID / Reference Number / Bank Reference Number.<br>Search is case sensitive - please search with the correct case to get the valid results.</p>
      <p v-else>{{emptyRecords}}</p>
    </div>

    <promotion-banner />
  </div>
</template>

<script>
import StatementDetails from '@/components/AccountStatement/StatementDetails'
import StatementFilter from '@/components/AccountStatement/StatementFilter'
import StatementPreferences from '@/components/AccountStatement/StatementPreferences'
import StatementExport from '@/components/global/ExportConfirmation'
import StatementSearch from '@/components/global/SearchRecords'
import StatementWebhooks from '@/components/global/BulkResendWebhooks'
import PromotionBanner from '@/components/global/PromotionBanner'

import ApiServices from '@/services/api/accountStatement'
import ListMixins from '@/mixins/listMixins'
import { mapGetters, mapMutations } from 'vuex'
import { amountFormat, capitalizeByDelimiter } from '@/utils/helpers'
import moment from 'moment'

export default {
  name: 'AccountTransactions',
  components: { StatementDetails, StatementFilter, StatementPreferences, StatementSearch, StatementExport, StatementWebhooks, PromotionBanner },
  mixins: [ListMixins],
  data () {
    return {
      txnOptions: [],
      txnFields: [],
      selectedFields: [],
      appliedFiltersObj: {},
      dateFilterFormat: 'Do MMM YY, H:mm:ss'
    }
  },
  props: {
    manageAccess: Object
  },
  created () {
    this.filterOptions = {
      start_time: moment().subtract(1, 'month').set({hour: 0, minute: 0, second: 0}).format('YYYY-MM-DD HH:mm:ss'),
      end_time: moment().set({hour: 23, minute: 59, second: 59}).format('YYYY-MM-DD HH:mm:ss')
    }
    this.SET_STATEMENT_FILTER(this.filterOptions)

    this.startProcessing()
  },
  computed: {
    ...mapGetters(['getterAccountBalance', 'getterBankTransferred'])
  },
  watch: {
    getterBankTransferred () {
      this.resetData()
      this.refetchBalance()
      this.filterOptions = {}
      this.populateData()
    },
    'manageAccess.accountBalance' (newVal, oldVal) {
      if (!oldVal && newVal) {
        this.refetchBalance()
      }
    },
    getterStatementFilter (newVal) {
      let filters = {}
      if (newVal.start_time) {
        filters['Date'] = `${moment(newVal.start_time).format(this.dateFilterFormat)} to ${moment(newVal.end_time).format(this.dateFilterFormat)}`
      }

      for (let key in newVal) {
        if (!['start_time', 'end_time', 'sort_order'].includes(key)) {
          filters[this.capitalizeByDelimiter(key)] = `(${newVal[key].length})`
        }
      }
      this.appliedFiltersObj = filters
    }
  },
  methods: {
    ...mapMutations(['SET_STATEMENT_FILTER', 'SET_ACCOUNT_BALANCE', 'SET_INVALID_RESPONSE_MSG']),
    amountFormat,
    capitalizeByDelimiter,
    populateParams () {
      this.api['bulkResendWebhook'] = ApiServices.bulkResendWebhook
      this.api['export'] = ApiServices.exportStatements
      this.api['search'] = ApiServices.search
      this.api['fetchStatus'] = ApiServices.transactionStatus
      this.keys['list'] = 'transactions'
      this.keys['popup'] = 'StatementDetails'
      this.paginate['token_type'] = 'next_page_token'
    },
    fetchRecords (start) {
      return new Promise(resolve => {
        let params = {
          ...this.filterOptions
        }
        if (start) {
          params['next_page_token'] = this.paginate['next_page_token']
        }

        this.SET_INVALID_RESPONSE_MSG(null)
        ApiServices.accountStatements(params).then((response) => {
          this.SET_STATEMENT_FILTER(this.filterOptions)

          let data = response.data
          if (data) {
            if (start) {
              this.list.push(...data.transactions)
              if (data.next_page_token) {
                this.paginate['next_page_token'] = data['next_page_token']
              } else {
                this.paginate['stop'] = true
              }
            } else {
              this.list = data.transactions
              this.populateMeta(response)
              this.setPaginate(data)
              this.paginate['privileges_changed'] = false
              this.$emit('balanceAccess', response.privileges.account_balance)
            }
            resolve()
          } else if (response.privileges_changed) {
            this.list = []
            this.selectedFields = []
            this.SET_INVALID_RESPONSE_MSG(response.message)
            this.paginate['privileges_changed'] = true
            this.paginate['total'] = null
          }
        })
      })
    },
    populateMeta ({data}) {
      this.txnOptions = data.transaction_types
      this.txnFields = data.transaction_fields
      this.selectedFields = this.txnFields.filter(e => e.selected).map((e, index, arr) => {
        if (e.key === 'credit_debit') {
          e.shortenText = 'Cr./Dr.'
        } else {
          e.shortenText = e.text.replace('Transaction', 'Txn').replace('Amount', 'Amt').replace('Balance', 'Bal').replace('Payment', 'Pymt').replace('Reference', 'Ref')
        }
        e.isAmount = ['amount', 'charges_gst', 'settled_amount', 'closing_balance'].includes(e.key)
        e.css = ['status', 'created_at', 'txn_time'].includes(e.key) ? 'mt-grey-text' : ''
        if (arr.length > 5 && ['created_at', 'txn_time', 'txn_type'].includes(e.key)) {
          e.css += ' d-l'
        }
        if (index % 2 === 1) {
          e.css += ' mt-right'
        }
        return e
      })

      this.highlightSelectors = this.selectedFields.reduce((list, param, index) => {
        if (['id', 'reference_number', 'bank_ref_num'].includes(param.key)) {
          list.push(`.table-body .table-row p:nth-child(${index + 1}) span`)
        }
        return list
      }, [])
      this.highlightSelectors = this.highlightSelectors.join(', ')
    },
    refetchBalance () {
      if (this.manageAccess.accountBalance) {
        ApiServices.accountBalance().then((response) => {
          if (response.data) {
            this.SET_ACCOUNT_BALANCE(response.data.balance)
          } else {
            this.$emit('balanceAccess', false)
          }
        })
      }
    },
    refreshColumns () {
      this.$router.replace({query: {}})
      this.resetData()
      this.selectedFields = []
      this.openPopup(null)
      this.populateData()
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/table.scss';

  .table-row {
    p {
      word-break: break-word;
    }
  }

  .show-applied-filters {
    grid-column: 1 / span 2;
    display: flex;
    justify-content: flex-end;
    gap: 5px;

    .badge {
      border: 1px solid $grey-outline;
      border-radius: 10px;
      background: $grey-bg;
      display: flex;
      gap: 5px;
      padding: 5px;
      font-size: 14px;

      h5 {
        &:after {
          content: ':'
        }
      }
    }
  }

  .desktop {
    .filters {
      .options {
        display: flex;

        button {
          min-width: unset !important;

          &.preferences {
            padding: 10px !important;
          }

          .fa-cog {
            margin-left: 0 !important;
          }
        }
      }
    }

    .table-head, .table-row {
      gap: 5px;

      p {
        min-width: 100px;
      }
    }
  }

  .mobile, .tablet {
    .options {
      .preferences {
        flex: none !important;

        .fa-cog {
          margin: 0 10px !important;
        }
      }
    }

    .show-applied-filters {
      margin-top: 10px;
      flex-wrap: wrap;

      .badge {
        text-align: left;
      }
    }

    p {
      white-space: unset !important;
    }
  }
</style>
